import React from "react";
import Footer from "@/components/Footer";
import Navbar from "@/components/Navbar";
import { COTI_TESTNET_HEX } from "@/components/NetworkSelector";
import { KeyRound } from "lucide-react";

const DEFAULT_CONNECT_CHAIN =
  import.meta.env.VITE_DEFAULT_NETWORK_ID || COTI_TESTNET_HEX;

interface WelcomeScreenProps {
  onSelectChain: (chainHexId: string) => void;
}

// ── SVG gradient IDs (dark-mode purple→pink icon strokes) ─────────────────────
const DARK_GRAD = {
  shield:   "darkGrad_shield",
  transfer: "darkGrad_transfer",
  bolt:     "darkGrad_bolt",
};

// ── Inline SVG icons — each exports a light + dark variant ───────────────────

const ShieldLockIcon = () => (
  <svg width="38" height="38" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14 3L5 7V13.5C5 18.747 9.003 23.647 14 25C18.997 23.647 23 18.747 23 13.5V7L14 3Z"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    <rect x="10.5" y="12.5" width="7" height="5.5" rx="1" stroke="currentColor" strokeWidth="2.3" />
    <path d="M12 12.5V11C12 9.895 12.895 9 14 9C15.105 9 16 9.895 16 11V12.5" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" />
  </svg>
);
const ShieldLockIconDark = () => (
  <svg width="38" height="38" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id={DARK_GRAD.shield} x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#7c3aed" /><stop offset="100%" stopColor="#9333ea" />
      </linearGradient>
    </defs>
    <path d="M14 3L5 7V13.5C5 18.747 9.003 23.647 14 25C18.997 23.647 23 18.747 23 13.5V7L14 3Z"
      stroke={`url(#${DARK_GRAD.shield})`} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    <rect x="10.5" y="12.5" width="7" height="5.5" rx="1" stroke={`url(#${DARK_GRAD.shield})`} strokeWidth="2.3" />
    <path d="M12 12.5V11C12 9.895 12.895 9 14 9C15.105 9 16 9.895 16 11V12.5" stroke={`url(#${DARK_GRAD.shield})`} strokeWidth="2.3" strokeLinecap="round" />
  </svg>
);

const ArrowsTransferIcon = () => (
  <svg width="38" height="38" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M7 10H21M21 10L17 6M21 10L17 14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M21 18H7M7 18L11 14M7 18L11 22" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const ArrowsTransferIconDark = () => (
  <svg width="38" height="38" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id={DARK_GRAD.transfer} x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#7c3aed" /><stop offset="100%" stopColor="#9333ea" />
      </linearGradient>
    </defs>
    <path d="M7 10H21M21 10L17 6M21 10L17 14" stroke={`url(#${DARK_GRAD.transfer})`} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M21 18H7M7 18L11 14M7 18L11 22" stroke={`url(#${DARK_GRAD.transfer})`} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const BoltIcon = () => (
  <svg width="38" height="38" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M15 4L7 16H14L13 24L21 12H14L15 4Z" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const BoltIconDark = () => (
  <svg width="38" height="38" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id={DARK_GRAD.bolt} x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#7c3aed" /><stop offset="100%" stopColor="#9333ea" />
      </linearGradient>
    </defs>
    <path d="M15 4L7 16H14L13 24L21 12H14L15 4Z" stroke={`url(#${DARK_GRAD.bolt})`} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const KeyRoundIcon = () => <KeyRound width={30} height={30} strokeWidth={2.5} />;
const KeyRoundIconDark = () => (
  <KeyRound className="text-purple-500" width={30} height={30} strokeWidth={2.5} />
);

// ── Feature card data ──────────────────────────────────────────────────────────

const features = [
  {
    icon: ShieldLockIcon,
    darkIcon: ShieldLockIconDark,
    title: "Private Tokens",
    description: "One-click shielded tokens",
  },
  {
    icon: ArrowsTransferIcon,
    darkIcon: ArrowsTransferIconDark,
    title: "Private Transfers",
    description: "Send and receive with privacy",
  },
  {
    icon: BoltIcon,
    darkIcon: BoltIconDark,
    title: "Powered by COTI",
    description: "Fast, easy & low-cost.",
  },
  {
    icon: KeyRoundIcon,
    darkIcon: KeyRoundIconDark,
    title: "Privacy Keys",
    description: "You control your privacy",
  },
] as const;

// ── Component ─────────────────────────────────────────────────────────────────

const WelcomeScreen: React.FC<WelcomeScreenProps> = ({ onSelectChain }) => {
  const handleConnect = () => onSelectChain(DEFAULT_CONNECT_CHAIN);

  return (
    <div className="welcome-bg min-h-screen flex flex-col">
      <Navbar welcomeMode />

      <main className="flex flex-1 flex-col items-center justify-center px-4 py-8 sm:px-6 sm:py-12">
        <section className="welcome-hero relative w-full max-w-6xl text-center">
          <img
            src="/coti-cool-logo.svg"
            alt=""
            aria-hidden="true"
            className="pointer-events-none absolute right-0 top-0 hidden h-28 w-36 select-none object-contain opacity-90 lg:block"
          />

          <div className="relative z-10 mx-auto max-w-2xl">
            <h1 className="text-2xl font-bold tracking-tight text-foreground sm:text-4xl">
              Get started: Connect your Wallet
            </h1>
            <p className="mx-auto mt-3 max-w-md text-xs leading-relaxed text-muted-foreground sm:text-sm">
              <span className="block">Turn your tokens private, manage your assets</span>
              <span className="block">and experience the power of onchain privacy.</span>
            </p>

            <ol aria-label="Getting started" className="mx-auto mt-7 flex max-w-xl items-start sm:mt-9">
              {["Connect wallet", "Select asset", "Make it private"].map((step, index) => (
                <li key={step} className="welcome-step-item relative flex min-w-0 flex-1 flex-col items-center gap-2">
                  <span
                    className={`welcome-step-circle ${index === 0 ? "welcome-step-circle-active" : ""}`}
                    aria-current={index === 0 ? "step" : undefined}
                  >
                    {index + 1}
                  </span>
                  <span className="text-xs font-medium text-muted-foreground sm:text-sm">
                    {step}
                  </span>
                </li>
              ))}
            </ol>

            <button
              onClick={handleConnect}
              className="welcome-connect-button btn-gradient-connect mx-auto mt-7 flex h-12 w-full max-w-sm items-center justify-center gap-2 rounded-lg text-sm font-semibold text-white transition-all duration-200 hover:opacity-95 sm:mt-9 sm:text-base"
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <rect x="3.5" y="6.5" width="17" height="13" rx="2" stroke="currentColor" strokeWidth="1.6" />
                <path d="M6 6.5V5.75A2.25 2.25 0 0 1 8.25 3.5h8A2.25 2.25 0 0 1 18.5 5.75v.75" stroke="currentColor" strokeWidth="1.6" />
                <circle cx="16.5" cy="13" r="1.25" fill="currentColor" />
              </svg>
              Connect Wallet
            </button>

            <p className="mt-3 flex items-center justify-center gap-1.5 text-[10px] text-muted-foreground sm:text-xs">
              <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <rect x="5" y="10" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="1.8" />
                <path d="M8 10V7a4 4 0 0 1 8 0v3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
              Secure, non-custodial, and private by design.
            </p>
          </div>

          <div className="mx-auto mt-8 grid w-full max-w-5xl grid-cols-2 gap-3 sm:mt-12 sm:grid-cols-4 sm:gap-4">
            {features.map(({ icon: Icon, darkIcon: DarkIcon, title, description }) => (
              <article key={title} className="welcome-feature-card flex min-h-32 flex-col items-center justify-start rounded-xl p-3 pt-5 text-center sm:min-h-40 sm:p-4 sm:pt-6">
                <div className="welcome-feature-icon mb-3 flex h-11 w-11 items-center justify-center rounded-full text-[#5271FF] dark:hidden sm:h-14 sm:w-14">
                  <Icon />
                </div>
                <div className="welcome-feature-icon dark-feature-icon mb-3 hidden h-11 w-11 items-center justify-center rounded-full dark:flex sm:h-14 sm:w-14">
                  <DarkIcon />
                </div>
                <h2 className="text-xs font-semibold text-foreground sm:text-sm">{title}</h2>
                <p className="mt-1 text-[10px] leading-snug text-muted-foreground sm:text-xs">{description}</p>
              </article>
            ))}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default WelcomeScreen;
